import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import { Container, Row, Col } from 'reactstrap';

// Models
import { VehicleModel } from 'client/data/models/vehicle-v2';
import { UrlModel, URL_CONTEXT_PATH } from 'client/data/models/url';
import { getPageLinksPath, LinkWidgetsModel } from 'client/data/models/link-widgets';
import { setCriticalCss } from 'client/actions/critical-css';

// Utils
import { pageDefinition } from 'site-modules/shared/pages/page';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getModelState } from 'client/data/luckdragon/model';
import { TrackingHandler } from 'client/tracking/handler';
import { AppraisalMap, MMZ_TRACKING_IDS } from 'client/tracking/maps/appraisal';
import { getBaseBreadcrumb, getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getWordWithRightArticle } from 'client/utils/string-utils';
import { getMakeValueFaqEntries } from 'client/site-modules/appraisal/utils/make-value';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { getInitialTabIdFromLocation } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/get-initial-tab-id-from-location';

// Constants
import { URL_PATHS } from 'site-modules/shared/constants/appraisal/paths';
import { PAGE_DISPLAY_NAME as APPRAISAL_LANDING_PAGE_NAME } from 'client/site-modules/appraisal/pages/appraisal/appraisal';
import { WRAPPER_TYPES } from 'site-modules/shared/components/faq/constants';

// Components
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { AppraisalTabsContainer } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tabs-container';
import { SearchByModule } from 'site-modules/shared/components/search-by-module/search-by-module';
import { SuggestedVehiclePricingCarouselContainer } from 'site-modules/shared/components/suggested-vehicle-pricing-carousel/suggested-vehicle-pricing-carousel-container';
import { MakeValueInfoSection } from 'client/site-modules/appraisal/components/make-value-info-section/make-value-info-section';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';

import 'site-modules/shared/components/appraisal/appraisal-common.scss';
import 'client/site-modules/appraisal/pages/appraisal/appraisal.scss';

export class MakeValueUI extends Component {
  static propTypes = {
    isMobile: PropTypes.bool,
    makeName: PropTypes.string,
    makeSlug: PropTypes.string,
    location: PropTypes.shape({}),
  };

  static defaultProps = {
    isMobile: false,
    makeName: '',
    makeSlug: '',
    location: {},
  };

  componentDidMount() {
    TrackingHandler.useMap(AppraisalMap);
  }

  render() {
    const { makeName, makeSlug, isMobile, location } = this.props;
    const title = `${makeName} value appraisal`;
    const breadcrumbs = [
      getHomeBreadcrumb(),
      getBaseBreadcrumb(APPRAISAL_LANDING_PAGE_NAME, URL_PATHS.APPRAISAL_LANDING),
      getBaseBreadcrumb(title),
    ];

    return (
      <Container
        tag="main"
        id="main-content"
        tabIndex={-1}
        className="make-value appraisal"
        data-tracking-parent="make-value-page"
      >
        <Breadcrumbs className="ps-0 mb-2" data={breadcrumbs} />
        <h1 className="heading-2 mb-0_5">{title}</h1>
        <Row>
          <Col xs={12} lg={8}>
            <Row>
              <Col xs={12}>
                <ErrorBoundary>
                  <AppraisalTabsContainer
                    isMobile={isMobile}
                    className="mt-2 mb-0_5"
                    isYearDropdownFirst={false}
                    initialVehicle={{ make: makeSlug }}
                    isProfileOfferVinPrepopulationEnabled
                  />
                </ErrorBoundary>
              </Col>
            </Row>
            <hr className="mt-1_5 mb-2" />
            <ErrorBoundary>
              <MakeValueInfoSection makeName={makeName} />
            </ErrorBoundary>
            <hr className="mt-1_5 mb-2" />
            <section>
              <h2 className="heading-3 mb-0">Shop used cars</h2>
              <ErrorBoundary>
                <SearchByModule
                  location={location}
                  isMobile={isMobile}
                  trackingMap={MMZ_TRACKING_IDS}
                  isUsed
                  isSmallContainer={!isMobile}
                />
              </ErrorBoundary>
            </section>
            <hr className="mt-1_5 mb-2" />
            <Faq
              title="FAQ"
              entries={getMakeValueFaqEntries({ makeName })}
              wrapperType={WRAPPER_TYPES.accordion}
              pathname={location.pathname}
              generateLearnMoreLinks
            />
          </Col>
        </Row>
        <hr className="mt-1_5 mb-2" />
        <MinHeightWrapper className="suggested-pricing-wrapper" componentType="SUGGESTED_PRICING_CAROUSEL">
          <SuggestedVehiclePricingCarouselContainer className="px-0" />
        </MinHeightWrapper>
        <PageLinksWidget divideSections={false} title="Related information" hasTopDivider />
      </Container>
    );
  }
}

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  isAuthenticated: get(state.profile, 'isAuthenticated'),
});

export const stateToPropsConfig = {
  makeName: bindToPath('makes', VehicleModel, (makes, { makeSlug }) => get(makes, [makeSlug, 'name'])),
};

export const MakeValue = connect(mapStateToProps)(connectToModel(MakeValueUI, stateToPropsConfig));

export const pageDefinitionConfiguration = {
  name: 'used_cars_tmv_appraiser_make',
  category: 'used_cars_tmv_appraiser',
  preload: store => {
    const preloader = new ModelPreloader(store);

    preloader.resolve('makes', VehicleModel);
    preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

    preloader.set(URL_CONTEXT_PATH, UrlModel, {
      urlPattern: 'make-value',
    });
    return preloader.load().then(() => {
      const {
        pageContext: { location, page },
      } = store.getState();
      const initialTabId = getInitialTabIdFromLocation({
        location,
        isLicensePlateTabDefault: false,
        isSellCar: false,
        referer: page?.referer,
      });
      store.dispatch(setCriticalCss({ initialTabId }));
    });
  },
  seo: (state, props) => {
    const makeSlug = get(props, ['match', 'params', 'make'], '');
    const modelState = getModelState(state);
    const makeName = get(modelState.get('makes', VehicleModel), `${makeSlug}.name`);
    const makeNameWithArticle = getWordWithRightArticle(makeName);
    const pathname = get(props, ['location', 'pathname'], '');

    return {
      title: `What’s my ${makeName} worth? Get ${makeNameWithArticle} value appraisal online instantly | Edmunds`,
      description: `Edmunds' free ${makeName} value appraisal calculator gives you instant used car, truck and SUV values. Whether you're trading your ${makeName} in at a dealer or planning to sell it, Edmunds makes it easy to understand your ${makeName}'s value and get a great deal.`,
      jsonld: [
        getBreadcrumbSchema([
          getHomeBreadcrumb(),
          getHomeBreadcrumb(),
          getBaseBreadcrumb(APPRAISAL_LANDING_PAGE_NAME, URL_PATHS.APPRAISAL_LANDING),
          getBaseBreadcrumb(`${makeName} value appraisal`, pathname),
        ]),
        getFAQSchema({
          entries: getMakeValueFaqEntries({ makeName }),
          generateLearnMoreLinks: true,
          pathname,
        }),
      ],
    };
  },
  component: props => <MakeValue {...props} makeSlug={get(props, ['match', 'params', 'make'], '')} />,
};

export default pageDefinition(pageDefinitionConfiguration);
